import { injectable } from 'inversify';
import BaseService from '@core/services/base';
import { AxiosInstance } from 'axios';
import HttpClient from '@core/HttpClient';
import container from '@core/di';
import Config from '@core/config';
import { TreatmentPlanMetadataDto } from '@shared/models/treatment-plan-metadata';

@injectable()
export default class TreatmentPlansService extends BaseService {
  static diToken = Symbol('treatment-plans');
  private httpClient: AxiosInstance;

  constructor() {
    super({ domainArea: 'treatment-plans' });
    this.httpClient = container.get<HttpClient>(HttpClient.diToken).getInstance();
  }

  getTreatmentPlanMetadata = async (
    childId: string | number,
  ): Promise<TreatmentPlanMetadataDto> => {
    const config = container.get<Config>(Config.diToken);
    const { clientApiURL } = config.get();

    const { data } = await this.httpClient({
      method: 'get',
      baseURL: `${clientApiURL.origin}${clientApiURL.pathname}`,
      url: `/child/${childId}/treatment-plans/current/metadata`,
    });

    return data;
  };

  async getNotApprovedTreatmentPlans(page: string, pageSize: string) {
    const config = container.get<Config>(Config.diToken);
    const { clientApiURL } = config.get();
    const { data } = await this.httpClient({
      method: 'get',
      baseURL: `${clientApiURL.origin}${clientApiURL.pathname}`,
      url: this.getURL(`not-approved?Page=${page}&PageSize=${pageSize}`),
    });

    return data;
  }

  async GetApprovedTreatmentPlans(page: string, pageSize: string, childId: number) {
    const config = container.get<Config>(Config.diToken);
    const { clientApiURL } = config.get();
    const { data } = await this.httpClient(
      `${clientApiURL.origin}${clientApiURL.pathname}/child/${childId}/treatment-plans/approved`,
    );

    return data;
  }

  async ApproveTreatmentPlan(childId: number) {
    const config = container.get<Config>(Config.diToken);
    const { clientApiURL } = config.get();
    const { data } = await this.httpClient({
      method: 'put',
      baseURL: `${clientApiURL.origin}${clientApiURL.pathname}`,
      url: `child/${childId}/treatment-plans/current/approve`,
    });

    return data;
  }

  async RejectTreatmentPlan(childId: number, rejectiontNote: string) {
    const config = container.get<Config>(Config.diToken);
    const { clientApiURL } = config.get();
    const { data } = await this.httpClient.put(
      `${clientApiURL.origin}${clientApiURL.pathname}/child/${childId}/treatment-plans/current/reject`,
      { rejectNote: rejectiontNote },
    );

    return data;
  }

  async RegenerateTreatmentPlan(childId: number) {
    const config = container.get<Config>(Config.diToken);
    const { clientApiURL } = config.get();
    const { data } = await this.httpClient.put(
      `${clientApiURL.origin}${clientApiURL.pathname}/child/${childId}/treatment-plans/regenerate`,
    );

    return data;
  }

  async GetTreatmentOptions(childId: number) {
    const config = container.get<Config>(Config.diToken);
    const { clientApiURL } = config.get();
    const { data } = await this.httpClient.get(
      `${clientApiURL.origin}${clientApiURL.pathname}/child/${childId}/treatment-plans/current/treatment-options`,
    );

    return data;
  }

  async GetRejectedTreatmentPlans(assessmentId: number) {
    const config = container.get<Config>(Config.diToken);
    const { clientApiURL } = config.get();
    const { data } = await this.httpClient.get(
      `${clientApiURL.origin}${clientApiURL.pathname}/assessments/${assessmentId}/treatment-plans`,
    );

    return data;
  }

  async GetTreatmentPlanOptions(treatmentPlanId: number) {
    const config = container.get<Config>(Config.diToken);
    const { clientApiURL } = config.get();
    const { data } = await this.httpClient.get(
      `${clientApiURL.origin}${clientApiURL.pathname}/treatment-plans/${treatmentPlanId}/treatment-options`,
    );

    return data;
  }

  async downloadAssessmentSpreadsheet(assessmentId: number) {
    const config = container.get<Config>(Config.diToken);
    const { clientApiURL } = config.get();
    const data = await this.httpClient.get(
      `${clientApiURL.origin}${clientApiURL.pathname}/assessments/${assessmentId}/treatment-plan-model`,
      { responseType: 'blob' },
    );

    const response = { data: data.data, headers: data.headers };

    let fileName = '';
    let blob;

    let tempFileName = response.headers['content-disposition'];
    tempFileName = tempFileName.substring(22);

    const endIndex = tempFileName.indexOf('"');
    fileName = tempFileName.substring(0, endIndex);
    blob = response.data;

    return { fileName, blob };
  }

  async downloadAssessmentPdf(assessmentId: number, withNotes: boolean = false) {
    const config = container.get<Config>(Config.diToken);
    const { clientApiURL } = config.get();
    const data = await this.httpClient.get(
      `${clientApiURL.origin}${clientApiURL.pathname}/assessments/${assessmentId}/download-pdf`,
      { responseType: 'blob', params: { withNotes } },
    );

    const fileName = `Assessment_${assessmentId}${withNotes ? '_with_notes' : ''}.pdf`;

    const blob = data.data;

    return { fileName, blob };
  }

  async downloadTreatmentPlanPdf(treatmentPlanId: number) {
    const config = container.get<Config>(Config.diToken);
    const { clientApiURL } = config.get();
    const data = await this.httpClient.get(
      `${clientApiURL.origin}${clientApiURL.pathname}/treatment-plans/${treatmentPlanId}/pdf`,
      { responseType: 'blob' },
    );

    const response = { data: data.data, headers: data.headers };

    const fileName = response.headers['content-disposition']
      .split(';')[1]
      .trim()
      .replaceAll(' ', '')
      .replaceAll('"', '')
      .replace('filename=', '');

    let blob = response.data;

    return { fileName, blob };
  }
}
